import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * ProductVariants Component
 * 
 * Manages product variants with a split layout:
 * - Left sidebar for product selection
 * - Right panel for variant details with dynamic columns
 * - Modal-based editing
 */
const ProductVariants = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    variant_name: '',
    sku: '',
    barcode: '',
    attribute_1_name: '',
    attribute_1_value: '',
    attribute_2_name: '',
    attribute_2_value: '',
    attribute_3_name: '',
    attribute_3_value: '',
    unit_price: '',
    cost: '',
    is_active: true
  });

  useEffect(() => {
    if (organization) {
      const loadData = async () => {
        loadColumnSettings();
        await loadProducts();
      };
      loadData();
    }
  }, [organization]);

  useEffect(() => {
    if (selectedProduct) {
      loadVariants(selectedProduct);
    }
  }, [selectedProduct]);

  // Handle panel resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = Math.max(200, Math.min(600, e.clientX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`productVariantsColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`productVariantsColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadVariants = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getProductVariants(productId, true);
      const variantsData = Array.isArray(response) ? response : (response.data || []);
      setVariants(variantsData);

      // Generate dynamic columns based on the first variant
      if (variantsData.length > 0) {
        const saved = localStorage.getItem(`productVariantsColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = variantsData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id' && key !== 'product_id' && key !== 'productId')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      setError(err.message || 'Failed to load variants');
      setVariants([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    saveColumnSettings(columnSettings, newSort);
  };

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`productVariantsColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    if (variants.length > 0) {
      const firstItem = variants[0];
      const columns = Object.keys(firstItem)
        .filter(key => key !== 'id' && key !== 'product_id' && key !== 'productId')
        .map((key, index) => ({
          key,
          label: formatColumnLabel(key),
          visible: true,
          order: index
        }));
      setTableColumns(columns);
    }
  };

  // Format cell values
  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // Handle dates
    if (key.includes('date') || key.includes('Date')) {
      return new Date(value).toLocaleDateString();
    }
    
    // Handle prices/costs
    if (key.includes('price') || key.includes('cost') || key.includes('Price') || key.includes('Cost')) {
      return `$${parseFloat(value).toFixed(2)}`;
    }
    
    // Handle active status
    if (key === 'is_active' || key === 'isActive') {
      return (
        <span className={`pm-badge ${value ? 'valid' : 'error'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      );
    }
    
    return String(value);
  };

  const handleOpenModal = (variant = null) => {
    setIsModalOpen(true);
    if (variant) {
      setEditingId(variant.id);
      setEditFormData({
        variant_name: variant.variant_name || variant.variantName || '',
        sku: variant.sku || variant.SKU || '',
        barcode: variant.barcode || '',
        attribute_1_name: variant.attribute_1_name || variant.attribute1Name || '',
        attribute_1_value: variant.attribute_1_value || variant.attribute1Value || '',
        attribute_2_name: variant.attribute_2_name || variant.attribute2Name || '',
        attribute_2_value: variant.attribute_2_value || variant.attribute2Value || '',
        attribute_3_name: variant.attribute_3_name || variant.attribute3Name || '',
        attribute_3_value: variant.attribute_3_value || variant.attribute3Value || '',
        unit_price: variant.unit_price || variant.unitPrice || '',
        cost: variant.cost || '',
        is_active: variant.is_active !== undefined ? variant.is_active : (variant.isActive !== undefined ? variant.isActive : true)
      });
    } else {
      setEditingId(null);
      setEditFormData({
        variant_name: '',
        sku: '',
        barcode: '',
        attribute_1_name: '',
        attribute_1_value: '',
        attribute_2_name: '',
        attribute_2_value: '',
        attribute_3_name: '',
        attribute_3_value: '',
        unit_price: '',
        cost: '',
        is_active: true
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setEditFormData({
      variant_name: '',
      sku: '',
      barcode: '',
      attribute_1_name: '',
      attribute_1_value: '',
      attribute_2_name: '',
      attribute_2_value: '',
      attribute_3_name: '',
      attribute_3_value: '',
      unit_price: '',
      cost: '',
      is_active: true
    });
  };

  const handleSave = async () => {
    if (!selectedProduct) {
      setError('Please select a product first');
      return;
    }

    try {
      setError(null);
      const dataToSave = {
        ...editFormData,
        product_id: selectedProduct
      };
      
      if (editingId) {
        await inventoryApi.updateProductVariant(editingId, editFormData);
      } else {
        await inventoryApi.createProductVariant(dataToSave);
      }
      await loadVariants(selectedProduct);
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save variant');
    }
  };

  const handleDelete = async (variantId) => {
    if (!window.confirm('Are you sure you want to delete this variant?')) {
      return;
    }

    try {
      setError(null);
      await inventoryApi.deleteProductVariant(variantId);
      await loadVariants(selectedProduct);
    } catch (err) {
      setError(err.message || 'Failed to delete variant');
    }
  };

  if (loading && products.length === 0) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_products')}
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="pm-page-card">
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
        <button className="pm-btn-primary" onClick={loadProducts}>
          {t('inventory.retry_button')}
        </button>
      </div>
    );
  }

  // Filter products for the side list
  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const term = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(term) ||
      product.sku?.toLowerCase().includes(term) ||
      product.SKU?.toLowerCase().includes(term)
    );
  });

  const selectedProductData = products.find(p => p.id === selectedProduct);

  // Filter variants based on search term
  const filteredVariants = variants.filter(variant => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(variant).some(value =>
      String(value).toLowerCase().includes(term)
    );
  });

  // Sort variants
  const sortedVariants = [...filteredVariants].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue === bValue) return 0;
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    const comparison = aValue < bValue ? -1 : 1;
    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const visibleColumns = tableColumns.filter(col => col.visible);

  return (
    <div className="pm-page-card pm-split-layout" style={{ '--sidebar-width': `${sidebarWidth}px` }}>
      {/* Product Selection Sidebar */}
      <div className="pm-sidebar">
        <h3>{t('inventory.products_heading')}</h3>
        <div className="pm-sidebar-search">
          <input
            type="search"
            placeholder={t('inventory.search_products_placeholder')}
            value={productSearchTerm}
            onChange={(e) => setProductSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-product-list-wrap">
          {filteredProducts.length === 0 ? (
            <div className="pm-empty-state compact">
              <p>{t('inventory.no_products_found')}</p>
            </div>
          ) : (
            <ul className="pm-product-list">
              {filteredProducts.map(product => (
                <li 
                  key={product.id}
                  onClick={() => setSelectedProduct(product.id)}
                  className={`pm-product-list-item ${selectedProduct === product.id ? 'selected' : ''}`}
                >
                  <div className="pm-product-list-name">{product.name}</div>
                  <div className="pm-product-list-sku">
                    {t('inventory.sku_prefix')} {product.sku || product.SKU || 'N/A'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Resizer */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className={`pm-resizer ${isResizing ? 'resizing' : ''}`}
      >
        <div className="pm-resizer-icon">⋮</div>
      </div>

      {/* Main Content Area */}
      <div className="pm-main-panel">
        <div className="pm-header">
          <h3 className="pm-header-title">
            {t('inventory.product_variants_title')}
            {selectedProductData && (
              <span className="pm-context-title">
                {t('inventory.product_variants_for')} {selectedProductData.name}
              </span>
            )}
          </h3>
          <button className="pm-btn-primary" onClick={() => handleOpenModal()} disabled={!selectedProduct}>
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_variant_button')}</span>
          </button>
        </div>

        {error && (
          <div className="pm-error-banner">
            <span aria-hidden="true">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <div className="pm-toolbar">
          <div className="pm-search-wrap">
            <input
              type="search"
              placeholder={t('inventory.search_variants_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {!selectedProduct ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
            <p>{t('inventory.select_product_message')}</p>
          </div>
        ) : variants.length === 0 && !loading ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">🔀</div>
            <h3>{t('inventory.no_variants_found')}</h3>
            <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
              <span aria-hidden="true">➕</span>
              {t('inventory.add_first_variant_button')}
            </button>
          </div>
        ) : (
          <div className="pm-table-wrap">
            <table className="pm-table" role="grid">
              <thead>
                <tr>
                  <th style={{ width: '100px' }}>
                    <div className="pm-header-content">
                      {tableColumns.filter(c => !c.visible).length > 0 && (
                        <button
                          className="pm-column-btn visibility-btn"
                          onClick={() => setShowColumnMenu(showColumnMenu === 'menu' ? null : 'menu')}
                          title={t('inventory.show_hidden_columns_tooltip')}
                        >
                          ⊕
                        </button>
                      )}
                      <div className="pm-header-label">{t('inventory.actions_header')}</div>
                    </div>
                    {showColumnMenu === 'menu' && (
                      <div className="pm-column-menu">
                        <div className="menu-header">{t('inventory.hidden_columns_header')}</div>
                        {tableColumns.filter(col => !col.visible).map(col => (
                          <button key={col.key} onClick={() => {
                            toggleColumnVisibility(col.key);
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.show_column_label')} {col.label}
                          </button>
                        ))}
                        <hr />
                        <button onClick={() => {
                          resetColumnSettings();
                          setShowColumnMenu(null);
                        }}>
                          {t('inventory.reset_all_button')}
                        </button>
                      </div>
                    )}
                  </th>
                  {visibleColumns.map((col, index) => (
                    <th key={col.key} className="pm-sortable-header">
                      <div className="pm-header-content">
                        <div className="pm-header-controls">
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnUp(col.key)}
                            disabled={index === 0}
                            title={t('inventory.move_left_tooltip')}
                          >
                            ◀
                          </button>
                          <button
                            className="pm-column-btn"
                            onClick={() => moveColumnDown(col.key)}
                            disabled={index === visibleColumns.length - 1}
                            title={t('inventory.move_right_tooltip')}
                          >
                            ▶
                          </button>
                          <button
                            className="pm-column-btn visibility-btn"
                            onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                            title={t('inventory.column_options_tooltip')}
                          >
                            ⋮
                          </button>
                        </div>
                        <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                          {col.label}
                          {sortConfig.key === col.key && (
                            <span className="pm-sort-indicator">
                              {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </div>
                      </div>
                      {showColumnMenu === col.key && (
                        <div className="pm-column-menu">
                          <button onClick={() => {
                            toggleColumnVisibility(col.key);
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.hide_column_option')}
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      <div className="pm-skeleton" aria-busy="true">{t('inventory.loading_variants')}</div>
                    </td>
                  </tr>
                ) : (
                  sortedVariants.map((variant) => (
                    <tr key={variant.id}>
                      <td>
                        <div className="pm-row-actions">
                          <button className="pm-row-btn edit" onClick={() => handleOpenModal(variant)} title={t('inventory.edit_tooltip')}>
                            <span aria-hidden="true">✏️</span>
                          </button>
                          <button className="pm-row-btn delete" onClick={() => handleDelete(variant.id)} title={t('inventory.delete_tooltip')}>
                            <span aria-hidden="true">🗑️</span>
                          </button>
                        </div>
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>
                          {formatCellValue(variant[col.key], col.key)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_variant_title') : t('inventory.add_variant_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                <div className="pm-form-group">
                  <label htmlFor="variant-name">{t('inventory.variant_name_label')}</label>
                  <input
                    id="variant-name"
                    type="text"
                    value={editFormData.variant_name}
                    onChange={(e) => setEditFormData({ ...editFormData, variant_name: e.target.value })}
                    required
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="variant-sku">{t('inventory.sku_label')}</label>
                  <input
                    id="variant-sku"
                    type="text"
                    value={editFormData.sku}
                    onChange={(e) => setEditFormData({ ...editFormData, sku: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="variant-barcode">{t('inventory.barcode_label')}</label>
                  <input
                    id="variant-barcode"
                    type="text"
                    value={editFormData.barcode}
                    onChange={(e) => setEditFormData({ ...editFormData, barcode: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="variant-price">{t('inventory.unit_price_label')}</label>
                  <input
                    id="variant-price"
                    type="number"
                    step="0.01"
                    value={editFormData.unit_price}
                    onChange={(e) => setEditFormData({ ...editFormData, unit_price: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="variant-cost">{t('inventory.cost_label')}</label>
                  <input
                    id="variant-cost"
                    type="number"
                    step="0.01"
                    value={editFormData.cost}
                    onChange={(e) => setEditFormData({ ...editFormData, cost: e.target.value })}
                  />
                </div>

                <div className="pm-subsection-title">{t('inventory.attributes_heading')}</div>
                
                <div className="pm-form-group">
                  <label htmlFor="attr-1-name">{t('inventory.attribute_1_name_label')}</label>
                  <input
                    id="attr-1-name"
                    type="text"
                    value={editFormData.attribute_1_name}
                    onChange={(e) => setEditFormData({ ...editFormData, attribute_1_name: e.target.value })}
                    placeholder={t('inventory.attribute_name_placeholder')}
                  />
                </div>
                <div className="pm-form-group">
                  <label htmlFor="attr-1-value">{t('inventory.attribute_1_value_label')}</label>
                  <input
                    id="attr-1-value"
                    type="text"
                    value={editFormData.attribute_1_value}
                    onChange={(e) => setEditFormData({ ...editFormData, attribute_1_value: e.target.value })}
                    placeholder={t('inventory.attribute_value_placeholder')}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="attr-2-name">{t('inventory.attribute_2_name_label')}</label>
                  <input
                    id="attr-2-name"
                    type="text"
                    value={editFormData.attribute_2_name}
                    onChange={(e) => setEditFormData({ ...editFormData, attribute_2_name: e.target.value })}
                  />
                </div>
                <div className="pm-form-group">
                  <label htmlFor="attr-2-value">{t('inventory.attribute_2_value_label')}</label>
                  <input
                    id="attr-2-value"
                    type="text"
                    value={editFormData.attribute_2_value}
                    onChange={(e) => setEditFormData({ ...editFormData, attribute_2_value: e.target.value })}
                  />
                </div>

                <div className="pm-form-group">
                  <label htmlFor="attr-3-name">{t('inventory.attribute_3_name_label')}</label>
                  <input
                    id="attr-3-name"
                    type="text"
                    value={editFormData.attribute_3_name}
                    onChange={(e) => setEditFormData({ ...editFormData, attribute_3_name: e.target.value })}
                  />
                </div>
                <div className="pm-form-group">
                  <label htmlFor="attr-3-value">{t('inventory.attribute_3_value_label')}</label>
                  <input
                    id="attr-3-value"
                    type="text"
                    value={editFormData.attribute_3_value}
                    onChange={(e) => setEditFormData({ ...editFormData, attribute_3_value: e.target.value })}
                  />
                </div>

                <div className="pm-toggle-row">
                  <label htmlFor="variant-active">
                    <input
                      id="variant-active"
                      type="checkbox"
                      role="switch"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    />
                    {t('inventory.active_label')}
                  </label>
                </div>
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal} type="button">
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave} type="button">
                {editingId ? t('inventory.update_category_button') : t('inventory.create_category_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductVariants;
